import React, { useEffect, useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTheme } from '../utils/theme';
export type CoolingOffStatus = 'loading' | 'active' | 'expired' | 'error';
interface CoolingOffScreenProps {
  coolingOffEndTime: number | null;
  coolingOffStartTime?: number | null;
  status?: CoolingOffStatus;
  onBack?: () => void;
  onContinue?: () => void;
  onRetry?: () => void;
  // Legacy props kept for API compatibility (no longer rendered)
  title?: string;
  description?: string;
  timerLabel?: string;
  buttonLabel?: string;
  completedTitle?: string;
  completedDescription?: string;
  completedButtonLabel?: string;
  showBackButton?: boolean;
}
const MAROON = '#661806';
const PAGE_BG = '#FFFBFA';
const LOGO_URL = 'https://cdn.magicpatterns.com/uploads/sKCkwA7xiDMeFZo7PBtgtZ/Whatsapp_digiBOP-removebg-preview.png';
export function CoolingOffScreen({
  coolingOffEndTime,
  status,
  onBack,
  onContinue
}: CoolingOffScreenProps) {
  const {
    isDark,
    isPlatinum
  } = useTheme();
  const [now, setNow] = useState(() => Date.now());
  // Tick every second — drives both clock + countdown
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  const derivedStatus: CoolingOffStatus = useMemo(() => {
    if (status) return status;
    if (!coolingOffEndTime) return 'loading';
    return now >= coolingOffEndTime ? 'expired' : 'active';
  }, [status, coolingOffEndTime, now]);
  // Countdown
  const secondsLeft = coolingOffEndTime ? Math.max(0, Math.floor((coolingOffEndTime - now) / 1000)) : 0;
  const h = Math.floor(secondsLeft / 3600);
  const m = Math.floor(secondsLeft % 3600 / 60);
  const s = secondsLeft % 60;
  const formatted = `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  // Live analog clock hands from real wall-clock time
  const date = new Date(now);
  const seconds = date.getSeconds();
  const minutes = date.getMinutes() + seconds / 60;
  const hours = date.getHours() % 12 + minutes / 60;
  const hourAngle = hours * 30; // 360/12
  const minuteAngle = minutes * 6; // 360/60
  const secondAngle = seconds * 6;
  // Theme-aware text color for headline (image shows near-black)
  const headlineColor = isDark || isPlatinum ? '#FFFFFF' : '#0F0F0F';
  const subColor = isDark || isPlatinum ? '#C4B5AA' : '#5A5A5A';
  const pageBg = isDark || isPlatinum ? 'linear-gradient(180deg, #1A0E08 0%, #2D1810 100%)' : PAGE_BG;
  // Render expired (timer hit zero) → green tint variant of same layout
  const isExpired = derivedStatus === 'expired';
  const isLoading = derivedStatus === 'loading';
  return <motion.div key="cooling-off" initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} className="w-full min-h-screen flex flex-col items-center" style={{
    background: pageBg
  }} data-id="element-858">
      {/* Logo */}
      <div className="w-full flex justify-center pt-8 pb-2 px-6" data-id="element-859">
        <img src={LOGO_URL} alt="digiBOP" style={{
        width: 135,
        height: 55,
        objectFit: 'contain',
        filter: isDark || isPlatinum ? 'brightness(0) invert(1)' : 'none'
      }} data-id="element-860" />
      </div>

      {/* Clock */}
      <div className="flex-1 flex flex-col items-center justify-start w-full px-6 pt-4" data-id="element-861">
        <AnalogClock hourAngle={hourAngle} minuteAngle={minuteAngle} secondAngle={secondAngle} color={MAROON} data-id="element-862" />

        {/* Headline + description */}
        <div className="text-center mt-10 px-2" data-id="element-863">
          <h1 style={{
          fontFamily: 'Poppins, system-ui, sans-serif',
          fontWeight: 400,
          fontSize: 32,
          lineHeight: 1.15,
          color: headlineColor,
          letterSpacing: '-0.5px'
        }} data-id="element-864">
            {isExpired ? "You're good to go!" : 'You are all set!'}
          </h1>
          <p className="mt-4 max-w-[320px] mx-auto" style={{
          fontFamily: 'Poppins, system-ui, sans-serif',
          fontWeight: 400,
          fontSize: 14,
          lineHeight: 1.55,
          color: subColor
        }} data-id="element-865">
            {isExpired ? <>
                Your cooling-off period has ended.
                <br data-id="element-866" />
                You can now continue to login securely.
              </> : <>
                Welcome aboard!
                <br data-id="element-867" />
                For your security, digiBOP will be accessible after cooling-off
                period.
              </>}
          </p>
        </div>

        {/* Countdown */}
        <div className="mt-7" data-id="element-868">
          {isLoading ? <div className="h-12 w-44 rounded-md" style={{
          background: 'rgba(102,24,6,0.08)'
        }} data-id="element-869" /> : <div style={{
          fontFamily: 'Poppins, system-ui, sans-serif',
          fontWeight: 700,
          fontSize: 44,
          letterSpacing: '-1px',
          color: '#D95A1A',
          fontVariantNumeric: 'tabular-nums'
        }} data-id="element-870">
              {isExpired ? '00:00:00' : formatted}
            </div>}
        </div>
      </div>

      {/* OK button */}
      <div className="w-full px-6 pb-8 pt-4 flex justify-center" data-id="element-871">
        <button onClick={onContinue ?? onBack} style={{
        width: 278,
        maxWidth: '100%',
        height: 56,
        borderRadius: 8,
        background: MAROON,
        color: '#FFFFFF',
        fontFamily: 'Poppins, system-ui, sans-serif',
        fontWeight: 500,
        fontSize: 16,
        border: 'none',
        cursor: 'pointer',
        boxShadow: '0 6px 16px rgba(102,24,6,0.25)'
      }} data-id="element-872">
          OK
        </button>
      </div>
    </motion.div>;
}
// ============ Analog clock ============
function AnalogClock({
  hourAngle,
  minuteAngle,
  secondAngle,
  color
}: {
  hourAngle: number;
  minuteAngle: number;
  secondAngle: number;
  color: string;
}) {
  const size = 200;
  const cx = size / 2;
  const cy = size / 2;
  const r = 92;
  // 12 hour ticks (longer at 12/3/6/9 not needed — image shows uniform short ticks)
  const ticks = Array.from({
    length: 12
  }).map((_, i) => {
    const angle = (i * 30 - 90) * (Math.PI / 180);
    const inner = r - 8;
    const outer = r - 2;
    return {
      x1: cx + Math.cos(angle) * inner,
      y1: cy + Math.sin(angle) * inner,
      x2: cx + Math.cos(angle) * outer,
      y2: cy + Math.sin(angle) * outer
    };
  });
  const hand = (length: number, angleDeg: number, width: number, key: string) => {
    const rad = (angleDeg - 90) * (Math.PI / 180);
    return <line key={key} x1={cx} y1={cy} x2={cx + Math.cos(rad) * length} y2={cy + Math.sin(rad) * length} stroke={color} strokeWidth={width} strokeLinecap="round" data-id="element-873" />;
  };
  return <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} aria-label="Cooling off clock" data-id="element-874">
      {/* Outer ring */}
      <circle cx={cx} cy={cy} r={r} fill="none" stroke={color} strokeWidth={3} data-id="element-875" />
      {/* Ticks */}
      {ticks.map((t, i) => <line key={i} x1={t.x1} y1={t.y1} x2={t.x2} y2={t.y2} stroke={color} strokeWidth={2} strokeLinecap="round" data-id="element-876" />)}
      {/* Hour hand */}
      {hand(r * 0.5, hourAngle, 3.5, 'h')}
      {/* Minute hand */}
      {hand(r * 0.72, minuteAngle, 2.5, 'm')}
      {/* Optional thin second hand — subtle, same color */}
      {hand(r * 0.78, secondAngle, 1, 's')}
      {/* Center dot */}
      <circle cx={cx} cy={cy} r={2.5} fill={color} data-id="element-877" />
    </svg>;
}